import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, DbNewsItem } from "../../lib/supabase";
import { LangData, saveNewsTranslations, loadNewsTranslations } from "../../lib/newsUtils";
import { useAuth } from "../../hooks/useAuth";
import NewsForm from "./components/NewsForm";
import NewsTable from "./components/NewsTable";
import PendingApprovals from "./components/PendingApprovals";
import UserManagement from "./components/UserManagement";

// translationMap: { [newsId]: string[] } — language codes that have translations
// aiTranslationMap: { [newsId]: string[] } — language codes that are AI-translated
type TranslationMap = Record<string, string[]>;

export type AdminTab = 'news' | 'partners' | 'users';

export default function AdminPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('news');
  const [pendingCount, setPendingCount] = useState(0);

  const [news, setNews] = useState<DbNewsItem[]>([]);
  const [translationMap, setTranslationMap] = useState<TranslationMap>({});
  const [aiTranslationMap, setAiTranslationMap] = useState<TranslationMap>({});
  const [dataLoading, setDataLoading] = useState(true);
  const [modal, setModal] = useState<"none" | "add" | "edit" | "delete">("none");
  const [selected, setSelected] = useState<DbNewsItem | null>(null);
  const [selectedTranslations, setSelectedTranslations] = useState<Record<string, LangData>>({});
  const [selectedIsAITranslated, setSelectedIsAITranslated] = useState<Record<string, boolean>>({});
  const [formLoading, setFormLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login", { replace: true });
  }, [user, authLoading, navigate]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleToggleStatus = async (item: DbNewsItem) => {
    const newStatus = item.status === "published" ? "draft" : "published";
    const { error } = await supabase
      .from("news")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", item.id);
    if (error) {
      showToast(`Error: ${error.message}`, "error");
    } else {
      showToast(newStatus === "published" ? "Article is now published!" : "Article moved to drafts.");
      fetchNews();
    }
  };

  const fetchNews = useCallback(async () => {
    setDataLoading(true);
    const { data, error } = await supabase
      .from("news")
      .select("*")
      .order("date", { ascending: false });
    if (error) {
      showToast("Failed to load articles.", "error");
      setDataLoading(false);
      return;
    }
    const items = (data ?? []) as DbNewsItem[];
    setNews(items);

    // Fetch translation map in parallel
    if (items.length > 0) {
      const ids = items.map((n) => n.id);
      const { data: tData } = await supabase
        .from("news_translations")
        .select("news_id, language, is_ai_translated")
        .in("news_id", ids);
      const map: TranslationMap = {};
      const aiMap: TranslationMap = {};
      (tData ?? []).forEach((t: { news_id: string; language: string; is_ai_translated?: boolean }) => {
        if (!map[t.news_id]) map[t.news_id] = [];
        map[t.news_id].push(t.language);
        if (t.is_ai_translated) {
          if (!aiMap[t.news_id]) aiMap[t.news_id] = [];
          aiMap[t.news_id].push(t.language);
        }
      });
      setTranslationMap(map);
      setAiTranslationMap(aiMap);
    } else {
      setTranslationMap({});
      setAiTranslationMap({});
    }
    setDataLoading(false);
  }, []);

  useEffect(() => { if (user) fetchNews(); }, [user, fetchNews]);

  const handleAdd = async (
    main: Omit<DbNewsItem, "id" | "created_at" | "updated_at">,
    translations: Record<string, LangData>,
    isAITranslated: Record<string, boolean> = {}
  ) => {
    setFormLoading(true);
    const { data, error } = await supabase.from("news").insert(main).select().maybeSingle();
    if (error) {
      showToast(`Error: ${error.message}`, "error");
      setFormLoading(false);
      return;
    }
    if (data) await saveNewsTranslations((data as DbNewsItem).id, translations, isAITranslated);
    showToast("Article saved successfully!");
    setModal("none");
    fetchNews();
    setFormLoading(false);
  };

  const handleEdit = async (
    main: Omit<DbNewsItem, "id" | "created_at" | "updated_at">,
    translations: Record<string, LangData>,
    isAITranslated: Record<string, boolean> = {}
  ) => {
    if (!selected) return;
    setFormLoading(true);
    const { error } = await supabase
      .from("news")
      .update({ ...main, updated_at: new Date().toISOString() })
      .eq("id", selected.id);
    if (error) {
      showToast(`Error: ${error.message}`, "error");
      setFormLoading(false);
      return;
    }
    await saveNewsTranslations(selected.id, translations, isAITranslated);
    showToast("Article updated successfully!");
    setModal("none");
    setSelected(null);
    fetchNews();
    setFormLoading(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    setFormLoading(true);
    const { error } = await supabase.from("news").delete().eq("id", selected.id);
    if (error) {
      showToast(`Error: ${error.message}`, "error");
    } else {
      showToast("Article deleted.");
      setModal("none");
      setSelected(null);
      fetchNews();
    }
    setFormLoading(false);
  };

  const openEdit = async (item: DbNewsItem) => {
    setSelected(item);
    const { translations, isAITranslated } = await loadNewsTranslations(item.id);
    setSelectedTranslations(translations);
    setSelectedIsAITranslated(isAITranslated);
    setModal("edit");
  };

  const openDelete = (item: DbNewsItem) => {
    setSelected(item);
    setModal("delete");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-cool-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-navy/40 animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-cool-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-cool-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 flex items-center justify-center bg-navy rounded-md">
              <i className="ri-newspaper-line text-gold text-sm" />
            </div>
            <span className="text-sm font-bold text-navy tracking-tight">EU Next Grants</span>
            <span className="hidden sm:block text-navy/20 mx-1">|</span>
            <span className="hidden sm:block text-xs text-navy/40 font-medium">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-xs text-navy/40">{user.email}</span>
            <a
              href="/news"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-navy/50 hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line" />
              </span>
              View Site
            </a>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-navy/60 border border-cool-gray-200 rounded-md hover:bg-cool-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line" />
              </span>
              Sign Out
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 border-t border-cool-gray-100 flex items-center gap-1">
          <button
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'news' ? 'border-navy text-navy' : 'border-transparent text-navy/40 hover:text-navy/70'}`}
          >
            <i className="ri-newspaper-line" />
            News & Publications
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'partners' ? 'border-navy text-navy' : 'border-transparent text-navy/40 hover:text-navy/70'}`}
          >
            <i className="ri-user-add-line" />
            Partner Requests
            {pendingCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-500 text-white rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'users' ? 'border-navy text-navy' : 'border-transparent text-navy/40 hover:text-navy/70'}`}
          >
            <i className="ri-team-line" />
            User Management
          </button>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium animate-in slide-in-from-right-4 ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-close-circle-line"} />
          </span>
          {toast.msg}
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {activeTab === 'news' ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Articles", value: news.length, icon: "ri-article-line" },
                { label: "Published", value: news.filter((n) => n.status === "published").length, icon: "ri-global-line", color: "text-emerald-600" },
                { label: "Drafts", value: news.filter((n) => n.status === "draft").length, icon: "ri-draft-line", color: "text-amber-600" },
                { label: "With Deadline", value: news.filter((n) => n.deadline).length, icon: "ri-time-line" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-lg border border-cool-gray-200 px-6 py-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-navy/40 font-medium uppercase tracking-wider">{stat.label}</p>
                    <span className={`w-7 h-7 flex items-center justify-center ${stat.color ?? "text-navy/25"}`}>
                      <i className={`${stat.icon} text-base`} />
                    </span>
                  </div>
                  <p className={`text-3xl font-bold ${stat.color ?? "text-navy"}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Table card */}
            <div className="bg-white rounded-lg border border-cool-gray-200">
              <div className="flex items-center justify-between px-6 py-5 border-b border-cool-gray-200">
                <div>
                  <h2 className="text-base font-bold text-navy">News & Publications</h2>
                  <p className="text-xs text-navy/40 mt-0.5">Manage all articles and open calls</p>
                </div>
                <button
                  onClick={() => setModal("add")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line" />
                  </span>
                  Add Article
                </button>
              </div>
              {dataLoading ? (
                <div className="py-20 flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-loader-4-line text-2xl text-navy/30 animate-spin" />
                  </div>
                </div>
              ) : (
                <NewsTable items={news} translationMap={translationMap} aiTranslationMap={aiTranslationMap} onEdit={openEdit} onDelete={openDelete} onToggleStatus={handleToggleStatus} />
              )}
            </div>
          </>
        ) : activeTab === 'partners' ? (
          <PendingApprovals onCountChange={setPendingCount} />
        ) : (
          <UserManagement />
        )}
      </main>

      {/* Add / Edit Modal */}
      {(modal === "add" || modal === "edit") && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-white rounded-lg w-full max-w-2xl my-auto">
            <div className="flex items-center justify-between px-8 py-5 border-b border-cool-gray-200">
              <h3 className="text-lg font-bold text-navy">
                {modal === "add" ? "Add New Article" : "Edit Article"}
              </h3>
              <button
                onClick={() => { setModal("none"); setSelected(null); setSelectedTranslations({}); setSelectedIsAITranslated({}); }}
                className="w-8 h-8 flex items-center justify-center text-navy/40 hover:text-navy hover:bg-cool-gray-100 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-8 py-6">
              <NewsForm
                initial={modal === "edit" ? selected : null}
                initialTranslations={modal === "edit" ? selectedTranslations : {}}
                initialIsAITranslated={modal === "edit" ? selectedIsAITranslated : {}}
                onSubmit={modal === "add" ? handleAdd : handleEdit}
                onCancel={() => { setModal("none"); setSelected(null); setSelectedTranslations({}); setSelectedIsAITranslated({}); }}
                loading={formLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {modal === "delete" && selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-8">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-bold text-navy text-center mb-2">Delete Article?</h3>
            <p className="text-sm text-navy/55 text-center mb-6 leading-relaxed">
              "{selected.title}" will be permanently deleted and removed from the site.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setModal("none"); setSelected(null); }}
                className="flex-1 py-2.5 text-sm font-semibold text-navy/60 border border-cool-gray-200 rounded-md hover:bg-cool-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={formLoading}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {formLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
